"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STORAGE_BYTES_PER_GB = exports.PRODUCT_IMAGES_FREE_GB = exports.STORAGE_MODEL_IDS = exports.STORAGE_PROVIDER_ID = exports.DOCUMENT_STORAGE_ACTION_IDS = exports.DocumentVendorPrefPutRequestSchema = exports.DocumentPatchRequestSchema = exports.DocumentPoCheckSummarySchema = exports.DocumentCountsRequestSchema = exports.DocumentListQuerySchema = exports.DocumentUploadUrlRequestSchema = exports.DocumentVendorPrefSchema = exports.TenantDocumentActivitySchema = exports.TenantDocumentActivityActionSchema = exports.TENANT_DOCUMENT_ACTIVITY_ACTIONS = exports.TenantDocumentSchema = exports.InvoiceCheckSchema = exports.InvoiceLineDecisionSchema = exports.InvoiceCheckLineSchema = exports.InvoiceCheckFlagSchema = exports.INVOICE_CHECK_FLAGS = exports.DocumentInvoiceExtractionSchema = exports.DocumentInvoiceLineSchema = exports.InvoiceLineFacetsSchema = exports.INVOICE_FACETS = exports.DOCUMENT_DEFAULT_PRIVATE_ROLES = exports.TenantDocumentStatusSchema = exports.TENANT_DOCUMENT_STATUSES = exports.TENANT_DOCUMENT_TYPE_LABELS = exports.TenantDocumentTypeSchema = exports.TENANT_DOCUMENT_TYPES = exports.TenantDocumentVisibilitySchema = exports.TENANT_DOCUMENT_VISIBILITIES = exports.DOCUMENT_ALLOWED_CONTENT_TYPES = exports.DOCUMENT_VIEW_URL_TTL_SECONDS = exports.DOCUMENT_UPLOAD_URL_TTL_SECONDS = exports.DOCUMENT_MAX_BYTES = exports.DOCUMENTS_TIER_IDS = exports.DOCUMENTS_FAMILY_ID = exports.DOCUMENT_STORAGE_METER = exports.DOCUMENT_FEATURE = void 0;
exports.isAllowedDocumentContentType = isAllowedDocumentContentType;
const zod_1 = require("zod");
/**
 * Tenant Document Management — PO / GRN invoice attachments
 * (spec docs/superpowers/specs/2026-10-17-document-management-design.md).
 *
 * File is `tenantDocuments.ts` (not `documents.ts`): `documents.ts` already
 * holds the LEGAL document governance model. Likewise the visibility/type
 * names carry a `TenantDocument` prefix because `DocumentVisibility` is already
 * exported from ./systemDocuments. `DocumentVisibility` / `DocumentType` from
 * the spec are therefore `TenantDocumentVisibility` / `TenantDocumentType`.
 *
 * Rows live in `agenticpos-pos-dynamodb-procurement-documents-{stage}`
 * (PK tenantId, SK sk). Served by procurement-service `/documents/*`.
 */
/** Package feature gating every `/documents/*` route (front and back). */
exports.DOCUMENT_FEATURE = 'documents.invoices';
/** Meter resource on the documents package tiers (GB-month). */
exports.DOCUMENT_STORAGE_METER = 'document_storage_gb';
/** Package family + tier ids for Document Management. */
exports.DOCUMENTS_FAMILY_ID = 'fam_documents';
exports.DOCUMENTS_TIER_IDS = [
    'pkg_documents_basic',
    'pkg_documents_pro',
    'pkg_documents_advanced',
];
/** 25 MiB upload cap. */
exports.DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;
/** Presigned PUT lifetime (seconds). */
exports.DOCUMENT_UPLOAD_URL_TTL_SECONDS = 900;
/** Presigned GET lifetime (seconds). */
exports.DOCUMENT_VIEW_URL_TTL_SECONDS = 300;
/** PDF, Word, JPG/PNG/HEIC/WebP. */
exports.DOCUMENT_ALLOWED_CONTENT_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
    'image/webp',
];
function isAllowedDocumentContentType(ct) {
    return typeof ct === 'string' && exports.DOCUMENT_ALLOWED_CONTENT_TYPES.includes(ct.toLowerCase());
}
exports.TENANT_DOCUMENT_VISIBILITIES = ['private', 'shared'];
exports.TenantDocumentVisibilitySchema = zod_1.z.enum(exports.TENANT_DOCUMENT_VISIBILITIES);
exports.TENANT_DOCUMENT_TYPES = [
    'supplier_invoice',
    'delivery_note',
    'credit_memo',
    'statement',
    'other',
];
exports.TenantDocumentTypeSchema = zod_1.z.enum(exports.TENANT_DOCUMENT_TYPES);
exports.TENANT_DOCUMENT_TYPE_LABELS = {
    supplier_invoice: 'Supplier invoice',
    delivery_note: 'Delivery note',
    credit_memo: 'Credit memo',
    statement: 'Statement',
    other: 'Other',
};
exports.TENANT_DOCUMENT_STATUSES = ['pending', 'active', 'removed'];
exports.TenantDocumentStatusSchema = zod_1.z.enum(exports.TENANT_DOCUMENT_STATUSES);
/** Default roles that may open a PRIVATE document when the vendor has no pref. */
exports.DOCUMENT_DEFAULT_PRIVATE_ROLES = ['owner', 'admin', 'manager'];
const IsoDate = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
/**
 * What Ruby read off an invoice (documents.read_invoice, 2026-10-17). Every field is
 * optional — Ruby leaves out what it cannot read. Money in signed integer cents.
 */
exports.INVOICE_FACETS = ['brand', 'productType', 'flavor', 'strength', 'size'];
/** 2026-10-18 (additive) — attributes Ruby read off an invoice line description. */
exports.InvoiceLineFacetsSchema = zod_1.z.object({
    brand: zod_1.z.string().optional(),
    productType: zod_1.z.string().optional(),
    flavor: zod_1.z.string().optional(),
    strength: zod_1.z.string().optional(),
    size: zod_1.z.string().optional(),
    packCount: zod_1.z.number().optional(),
});
exports.DocumentInvoiceLineSchema = zod_1.z.object({
    description: zod_1.z.string(),
    quantity: zod_1.z.number().optional(),
    unit: zod_1.z.string().optional(),
    unitPriceCents: zod_1.z.number().int().optional(),
    totalCents: zod_1.z.number().int().optional(),
    /** 2026-10-18 (additive) — vendor item code / UPC as printed. */
    itemCode: zod_1.z.string().optional(),
    /** 2026-10-18 (additive) — facets read from the description. */
    facets: exports.InvoiceLineFacetsSchema.optional(),
});
exports.DocumentInvoiceExtractionSchema = zod_1.z.object({
    vendorName: zod_1.z.string().optional(),
    invoiceNumber: zod_1.z.string().optional(),
    invoiceDate: zod_1.z.string().optional(),
    totalCents: zod_1.z.number().int().optional(),
    currency: zod_1.z.string().optional(),
    lineItems: zod_1.z.array(exports.DocumentInvoiceLineSchema).default([]),
    readAt: zod_1.z.string(),
    readById: zod_1.z.string().optional(),
    readByName: zod_1.z.string().optional(),
    runId: zod_1.z.string().optional(),
    tokensCharged: zod_1.z.number().optional(),
    modelId: zod_1.z.string().optional(),
});
/** 2026-10-18 — Ruby invoice check (invoice vs PO vs cost on file). */
exports.INVOICE_CHECK_FLAGS = [
    'cost_up', 'cost_down', 'qty_short', 'qty_over', 'not_ordered', 'not_invoiced',
];
exports.InvoiceCheckFlagSchema = zod_1.z.enum(exports.INVOICE_CHECK_FLAGS);
exports.InvoiceCheckLineSchema = zod_1.z.object({
    lineIndex: zod_1.z.number().nullable(),
    description: zod_1.z.string().optional(),
    variantId: zod_1.z.string().optional(),
    productName: zod_1.z.string().optional(),
    matchedBy: zod_1.z.enum(['barcode', 'sku', 'item_code', 'name', 'none', 'remembered', 'manual', 'suggested']),
    orderedQty: zod_1.z.number().optional(),
    invoicedQty: zod_1.z.number().optional(),
    costOnFileCents: zod_1.z.number().optional(),
    invoiceCostCents: zod_1.z.number().optional(),
    retailCents: zod_1.z.number().optional(),
    marginBeforePct: zod_1.z.number().optional(),
    marginAfterPct: zod_1.z.number().optional(),
    flags: zod_1.z.array(exports.InvoiceCheckFlagSchema),
    /** 2026-10-18 (additive) — Ruby's best guess when matchedBy is 'suggested'. */
    suggestion: zod_1.z.object({
        variantId: zod_1.z.string(),
        productName: zod_1.z.string(),
        score: zod_1.z.number(),
        facets: zod_1.z.record(zod_1.z.enum(exports.INVOICE_FACETS), zod_1.z.enum(['yes', 'no', 'unknown'])),
    }).optional(),
    alternatives: zod_1.z.array(zod_1.z.object({
        variantId: zod_1.z.string(),
        productName: zod_1.z.string(),
        score: zod_1.z.number(),
    })).optional(),
    /** Units per invoiced pack, singles invoiced (qty x pack) and cost per single. */
    packSize: zod_1.z.number().optional(),
    invoicedSingles: zod_1.z.number().optional(),
    singleCostCents: zod_1.z.number().optional(),
    needsReview: zod_1.z.boolean().optional(),
});
/** 2026-10-18 — a person's answer on one invoice line (confirm Ruby's guess, pick, or not ours). */
exports.InvoiceLineDecisionSchema = zod_1.z.object({
    action: zod_1.z.enum(['confirm', 'pick', 'not_ours']),
    variantId: zod_1.z.string().optional(),
    packSize: zod_1.z.number().optional(),
    byId: zod_1.z.string(),
    at: zod_1.z.string(),
});
exports.InvoiceCheckSchema = zod_1.z.object({
    checkedAt: zod_1.z.string(),
    poId: zod_1.z.string(),
    locationIds: zod_1.z.array(zod_1.z.string()),
    lines: zod_1.z.array(exports.InvoiceCheckLineSchema),
    counts: zod_1.z.object({
        matched: zod_1.z.number(),
        costChanged: zod_1.z.number(),
        qtyDiffers: zod_1.z.number(),
        notOrdered: zod_1.z.number(),
        notInvoiced: zod_1.z.number(),
        suggested: zod_1.z.number().optional(),
        needsReview: zod_1.z.number().optional(),
    }),
    invoiceTotalCents: zod_1.z.number().optional(),
    poTotalCents: zod_1.z.number().optional(),
    status: zod_1.z.enum(['clean', 'flagged', 'unreadable']),
    /** Entries are `${lineIndex}:${variantId}:${flag}`. */
    resolvedFlags: zod_1.z.array(zod_1.z.string()).optional(),
    /** 2026-10-18 (additive) — keyed by String(lineIndex). */
    lineDecisions: zod_1.z.record(zod_1.z.string(), exports.InvoiceLineDecisionSchema).optional(),
    vendorMismatch: zod_1.z.object({ invoiceVendor: zod_1.z.string(), orderVendor: zod_1.z.string() }).optional(),
});
exports.TenantDocumentSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    documentId: zod_1.z.string(), // doc_<12>
    status: exports.TenantDocumentStatusSchema,
    s3Key: zod_1.z.string(),
    fileName: zod_1.z.string(),
    contentType: zod_1.z.string(),
    sizeBytes: zod_1.z.number().int().nonnegative(),
    docType: exports.TenantDocumentTypeSchema,
    invoiceNumber: zod_1.z.string().optional(),
    invoiceDate: IsoDate.optional(),
    /** Signed integer cents (credit memos are negative). */
    amountCents: zod_1.z.number().int().optional(),
    vendorId: zod_1.z.string().optional(),
    vendorName: zod_1.z.string().optional(),
    poId: zod_1.z.string().optional(),
    poNumber: zod_1.z.string().optional(),
    grnId: zod_1.z.string().optional(),
    grnNumber: zod_1.z.string().optional(),
    locationId: zod_1.z.string().optional(),
    visibility: exports.TenantDocumentVisibilitySchema,
    uploadedById: zod_1.z.string(),
    uploadedByName: zod_1.z.string(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
    removedAt: zod_1.z.string().optional(),
    removedBy: zod_1.z.string().optional(),
    /** 2026-10-17 (additive) — what Ruby read off the file (documents.read_invoice). */
    extracted: exports.DocumentInvoiceExtractionSchema.optional(),
    /** 2026-10-18 (additive) — locations the invoice covers; absent/empty = all locations of the PO. */
    coverLocationIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** 2026-10-18 (additive) — Ruby invoice check result. */
    invoiceCheck: exports.InvoiceCheckSchema.optional(),
    /** 2026-10-18 (additive) — set by the list union when the document is inherited from a parent PO. */
    inheritedFrom: zod_1.z.object({ poId: zod_1.z.string(), poNumber: zod_1.z.string().optional() }).optional(),
});
exports.TENANT_DOCUMENT_ACTIVITY_ACTIONS = [
    'uploaded', 'viewed', 'downloaded', 'updated', 'visibility_changed', 'removed',
    // 2026-10-17 (additive) — Ruby read the file; the document was linked to a PO/GRN.
    'ruby_read', 'linked',
    // 2026-10-18 (additive) — Ruby invoice check ran; a finding was marked resolved.
    'invoice_checked', 'finding_resolved',
];
exports.TenantDocumentActivityActionSchema = zod_1.z.enum(exports.TENANT_DOCUMENT_ACTIVITY_ACTIONS);
exports.TenantDocumentActivitySchema = zod_1.z.object({
    documentId: zod_1.z.string(),
    action: exports.TenantDocumentActivityActionSchema,
    at: zod_1.z.string(),
    byId: zod_1.z.string(),
    byName: zod_1.z.string(),
    detail: zod_1.z.record(zod_1.z.unknown()).optional(),
});
exports.DocumentVendorPrefSchema = zod_1.z.object({
    vendorId: zod_1.z.string(),
    defaultVisibility: exports.TenantDocumentVisibilitySchema,
    privateRoles: zod_1.z.array(zod_1.z.string()),
    /** Ruby checks an attached invoice against its order automatically. Absent = ON. */
    autoRubyCheck: zod_1.z.boolean().optional(),
    updatedAt: zod_1.z.string().optional(),
    updatedBy: zod_1.z.string().optional(),
});
// ---------------- Requests / responses ----------------
/** POST /documents/upload-url */
exports.DocumentUploadUrlRequestSchema = zod_1.z.object({
    fileName: zod_1.z.string().min(1).max(255),
    contentType: zod_1.z.string().min(1),
    sizeBytes: zod_1.z.number().int().positive().max(exports.DOCUMENT_MAX_BYTES),
    docType: exports.TenantDocumentTypeSchema,
    poId: zod_1.z.string().optional(),
    grnId: zod_1.z.string().optional(),
    vendorId: zod_1.z.string().optional(),
    vendorName: zod_1.z.string().optional(),
    invoiceNumber: zod_1.z.string().max(100).optional(),
    invoiceDate: IsoDate.optional(),
    amountCents: zod_1.z.number().int().optional(),
    visibility: exports.TenantDocumentVisibilitySchema.optional(),
    locationId: zod_1.z.string().optional(),
    coverLocationIds: zod_1.z.array(zod_1.z.string()).max(50).optional(),
});
/** GET /documents query */
exports.DocumentListQuerySchema = zod_1.z.object({
    poId: zod_1.z.string().optional(),
    grnId: zod_1.z.string().optional(),
    vendorId: zod_1.z.string().optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    visibility: exports.TenantDocumentVisibilitySchema.optional(),
    docType: exports.TenantDocumentTypeSchema.optional(),
    q: zod_1.z.string().optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(200).optional(),
    cursor: zod_1.z.string().optional(),
});
/** POST /documents/counts */
exports.DocumentCountsRequestSchema = zod_1.z.object({
    poIds: zod_1.z.array(zod_1.z.string()).max(100).optional(),
    grnIds: zod_1.z.array(zod_1.z.string()).max(100).optional(),
});
/**
 * 2026-10-18 (additive) — per-PO Ruby invoice-check summary on /documents/counts, over the
 * same family union, documents the caller may open only. clean = checked, nothing open;
 * flagged = at least one unresolved flag; costChanged = unresolved cost_up/cost_down flags.
 */
exports.DocumentPoCheckSummarySchema = zod_1.z.object({
    clean: zod_1.z.number(),
    flagged: zod_1.z.number(),
    costChanged: zod_1.z.number(),
});
/** PATCH /documents/{id} */
exports.DocumentPatchRequestSchema = zod_1.z.object({
    docType: exports.TenantDocumentTypeSchema.optional(),
    invoiceNumber: zod_1.z.string().max(100).optional(),
    invoiceDate: IsoDate.optional(),
    amountCents: zod_1.z.number().int().optional(),
    visibility: exports.TenantDocumentVisibilitySchema.optional(),
    grnId: zod_1.z.string().optional(),
    /** null = reset to all locations. */
    coverLocationIds: zod_1.z.array(zod_1.z.string()).max(50).nullable().optional(),
});
/** GET · PUT /documents/vendor-prefs/{vendorId} */
exports.DocumentVendorPrefPutRequestSchema = zod_1.z.object({
    defaultVisibility: exports.TenantDocumentVisibilitySchema,
    privateRoles: zod_1.z.array(zod_1.z.string().min(1)).max(20),
    /** Absent = leave unchanged (ON when never set). */
    autoRubyCheck: zod_1.z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Phase 1B — storage metering into the Ruby Ledger (spec "Metering (phase 1B)").
// Charged monthly (UTC days 1–3) by procurement-service `storageMeter`.
// ---------------------------------------------------------------------------
/** Ruby actions the storage meter books against (one ledger line per item). */
exports.DOCUMENT_STORAGE_ACTION_IDS = [
    'storage.documents', 'storage.product_images', 'storage.requests', 'storage.egress',
];
/** Vendor (MODEL#) provider id for every storage action. */
exports.STORAGE_PROVIDER_ID = 'aws_s3';
/**
 * MODEL#aws_s3#<modelId> rows (AWS us-west-1 list price, priced via
 * `unitPrices.per_call_usd` so ai-router `computeCostUsd({calls: quantity})` applies):
 * - `storage_gb_month` — $0.026 per GB-month   (quantity = GB-months, fractional)
 * - `put_request`      — $0.0000055 per request ($0.0055 / 1,000 PUT; quantity = uploads)
 * - `get_request`      — $0.00000044 per request ($0.00044 / 1,000 GET; quantity = views + downloads)
 * - `egress_gb`        — $0.09 per GB out       (quantity = GB downloaded, fractional)
 * GB = 1e9 bytes throughout the meter.
 */
exports.STORAGE_MODEL_IDS = {
    storageGbMonth: 'storage_gb_month',
    putRequest: 'put_request',
    getRequest: 'get_request',
    egressGb: 'egress_gb',
};
/**
 * Product images: GB-months free for EVERY tenant before `storage.product_images`
 * meters (spec decision "1 GB free"). Constant today — owner-editable in Super Admin later.
 */
exports.PRODUCT_IMAGES_FREE_GB = 1;
/** Bytes per GB used by the storage meter (decimal GB). */
exports.STORAGE_BYTES_PER_GB = 1e9;
//# sourceMappingURL=tenantDocuments.js.map